import { Button } from "@/components/ui/button"

import { HouseRule, ManualSection, Plate, Swatch } from "./primitives"

// The Editorial Desk Palette: the working palette as a specimen sheet,
// the named house rules, and the two colors that join the desk with
// this page: stet green and red pencil.
export function PaletteSection() {
  return (
    <ManualSection
      id="palette"
      title="The Editorial Desk Palette"
      lede={
        <>
          Paper, ink, and a single highlighter, with blue held back for
          attribution. Code never uses raw hex; every color below is a token
          handle from <code className="font-mono text-sm">theme.css</code>.
        </>
      }
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 lg:grid-cols-5">
        <Swatch
          name="Oat"
          hex="#FBF6ED"
          token="surface-base · surface-default"
          role="Warm ground: base & canvas"
          swatchClassName="bg-surface-base"
        />
        <Swatch
          name="Newsprint Cream"
          hex="#FCF8EC"
          token="canvas-wash-warm"
          role="Atmospheric wash only"
          swatchClassName="bg-batyr-cream"
        />
        <Swatch
          name="Paper White"
          hex="#FFFFFF"
          token="surface-elevated"
          role="Cards & proofs"
          swatchClassName="bg-white"
        />
        <Swatch
          name="Manila"
          hex="#F0EBE2"
          token="surface-grouped"
          role="Grouping layer"
          swatchClassName="bg-surface-grouped"
        />
        <Swatch
          name="Ink"
          hex="#002555"
          token="text-primary"
          role="All text on light"
          swatchClassName="bg-batyr-ink"
        />
        <Swatch
          name="Masthead Navy"
          hex="#0B2555"
          token="surface-strong"
          role="Structure & weight"
          swatchClassName="bg-batyr-navy"
        />
        <Swatch
          name="Highlighter Yellow"
          hex="#FBC845"
          token="surface-action"
          role="The action moment"
          swatchClassName="bg-batyr-yellow"
        />
        <Swatch
          name="Byline Blue"
          hex="#3B5C85"
          token="surface-accent"
          role="Links · denim"
          swatchClassName="bg-batyr-blue"
        />
        <Swatch
          name="Batyr Mint"
          hex="#30DAC4"
          token="batyr-mint"
          role="Retired: do not use"
          swatchClassName="bg-batyr-mint"
          retired
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <HouseRule name="The Highlighter Rule">
          Yellow is a moment, not a mood. It marks the one primary action or the
          one thing that matters on a view. If yellow appears more than twice on
          a screen, the screen is wrong.
        </HouseRule>
        <HouseRule name="The Ink Rule">
          Text hierarchy is Ink at opacity steps: primary 100%, secondary 80%,
          tertiary 65%, quaternary 50%. It is never a gray ramp. Gray body text
          on cream is the washed-out look this system exists to avoid.
        </HouseRule>
        <HouseRule name="The Retired Mint Rule">
          Batyr mint is the sterile teal of our first anti-reference and the
          only cold high-chroma color in a warm print palette. The token still
          exists in code; do not reach for it. Stet green replaces it.
        </HouseRule>
      </div>

      <h3 className="text-primary mt-2 text-xl font-semibold">
        New to the desk
      </h3>

      <div className="grid gap-6 lg:grid-cols-2">
        <Plate
          title="Stet Green: let it stand"
          note="Affirmation, success, support. Named for the proofreader's mark that saves a line from deletion. White text on the solid; ink on the tint."
          importPath="bg-surface-affirm · bg-surface-affirm_tint · #3E7350 / #E7F0E7"
        >
          <div className="bg-surface-affirm_tint flex flex-col gap-3 rounded-lg p-5">
            <div className="flex items-center gap-2.5">
              <span className="bg-surface-affirm text-on-affirm inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold">
                Stet · let it stand
              </span>
              <span className="text-batyr-stet-green text-sm font-semibold">
                Suggestion accepted
              </span>
            </div>
            <p className="text-secondary max-w-prose text-sm">
              &ldquo;The Night We Met&rdquo; now holds slot 4 of{" "}
              <span className="font-medium">
                Songs that got me through the first year
              </span>
              . Theo&rsquo;s note travels with it.
            </p>
          </div>
          <p className="text-tertiary text-sm">
            Stet green affirms what the community made. It is never a wash, and
            it never replaces UUI&rsquo;s error crimson, which stays semantic.
          </p>
        </Plate>

        <Plate
          title="Red Pencil: marginalia"
          note="Contributor presence and warm energy in the margins. Strictly non-semantic: errors stay on UUI's error crimson."
          importPath="text-batyr-red-pencil · #C75B39"
        >
          <div className="flex flex-col gap-2">
            <p className="text-secondary max-w-prose text-sm">
              My sister sent me this with the message &lsquo;you don&rsquo;t
              have to reply&rsquo;. That permission mattered more than the song.
            </p>
            <p className="text-sm">
              <span
                aria-hidden="true"
                className="text-batyr-red-pencil font-semibold"
              >
                ✑{" "}
              </span>
              <span className="text-primary decoration-batyr-red-pencil font-semibold underline decoration-2 underline-offset-4">
                Mara Quinn
              </span>
              <span className="text-tertiary"> annotated this track</span>
            </p>
          </div>
          <p className="text-tertiary text-sm">
            At 3.9:1 on cream, red pencil is for marks rather than type:
            underlines, strikes, and glyphs beside Ink words. It never carries
            body copy, and it never means &ldquo;error&rdquo;.
          </p>
        </Plate>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Plate
          title="On navy: the _on-brand hierarchy"
          note="Text on strong surfaces is white stepped by opacity, mirroring the Ink steps on light."
          importPath="text-primary_on-brand → text-quaternary_on-brand"
          bodyClassName="p-0"
        >
          <div className="bg-brand-section flex flex-col gap-1 p-6">
            <p className="text-primary_on-brand text-md font-semibold">
              Primary: headlines and names
            </p>
            <p className="text-secondary_on-brand text-md">
              Secondary: supporting copy at 80%
            </p>
            <p className="text-tertiary_on-brand text-md">
              Tertiary: metadata at 65%
            </p>
            <p className="text-quaternary_on-brand text-md">
              Quaternary: subtle hints at 50%
            </p>
          </div>
        </Plate>

        <Plate
          title="The highlighter in action"
          note="One yellow moment per view: the primary action. Everything else steps back so it can land."
        >
          <div className="flex flex-wrap items-center gap-3">
            <Button color="primary" size="md">
              Suggest a track
            </Button>
            <Button color="secondary" size="md">
              Share this brief
            </Button>
            <Button color="link-color" size="md">
              Read the curator&rsquo;s note
            </Button>
          </div>
          <p className="text-tertiary text-sm">
            Yellow always carries Ink text, enforced globally via{" "}
            <code className="font-mono text-xs">.bg-brand-solid</code>. The
            focus ring stays Byline Blue, because yellow disappears on cream.
          </p>
        </Plate>
      </div>
    </ManualSection>
  )
}
